"use client"

import { addAComment } from "@/actions/vote"
import { useFormik } from "formik"
import { useCallback, useState } from "react"
import { z } from "zod"
import { toFormikValidationSchema } from "zod-formik-adapter"

import { useToast } from "@/hooks/use-toast"

import { Button } from "./ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog"
import { Slider } from "./ui/slider"
import { Textarea } from "./ui/textarea"

const schema = z.object({
  rating: z.number().min(1).max(5),
  comment: z.string().optional(),
})

export default function VoteModal({
  isOpen,
  setIsOpen,
  boilerplateId,
}: {
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
  boilerplateId: string
}) {
  const { toast } = useToast()
  const formik = useFormik({
    initialValues: {
      rating: 5,
      comment: "",
    },
    validationSchema: toFormikValidationSchema(schema),
    onSubmit: (params) => {
      addAComment({
        boilerplateId,
        comment: params.comment,
        shipSpeed: params.rating,
      })
        .catch((err) => {
          console.error(err)
          toast({
            title: "Error",
            description: "An error occurred while adding your review",
          })
        })
        .then((el) => {
          toast({
            title: "Success",
            description: "Your review has been added successfully",
          })
          setIsOpen(false)
        })
    },
  })
  return (
    <Dialog
      open={isOpen}
      onOpenChange={(val) => {
        setIsOpen(val)
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Leave a review</DialogTitle>
          <DialogDescription>
            Your opinion is important to help users really choose the best
            boilerplate of all time. Don&apos;t be too nice 😉
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <Slider
            value={[formik.values.rating]}
            onValueChange={(val) => {
              formik.setFieldValue("rating", val[0])
            }}
            step={1}
            label="Shipping speed*"
            min={1}
            max={5}
          />
          <div className="flex w-full justify-between px-2 text-xs text-main-black">
            <span>Slow as hell</span>
            <span>Slow</span>
            <span>Fast</span>
            <span>Fast as hell</span>
            <span>Light-speed</span>
          </div>
        </div>
        <Textarea
          hint={formik.errors.comment}
          value={formik.values.comment}
          onChange={(e) => {
            formik.setFieldValue("comment", e.target.value)
          }}
          label="Leave a comment"
        />
        <DialogFooter>
          <Button
            onClick={() => {
              formik.handleSubmit()
            }}
            type="submit"
          >
            Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export function useVoteModal(boilerplateId: string) {
  const [isOpen, setIsOpen] = useState(false)

  function open() {
    setIsOpen(true)
  }

  function close() {
    setIsOpen(false)
  }

  const modal = useCallback(() => {
    return (
      <VoteModal
        setIsOpen={setIsOpen}
        isOpen={isOpen}
        boilerplateId={boilerplateId}
      />
    )
  }, [isOpen, boilerplateId, setIsOpen])

  return { isOpen, open, close, modal }
}
